/**
 * Crypto Price categories are one leaf per asset and window (`bitcoin-15min`).
 * The sidebar reads those leaves as two filters. `15min` is matched before `5min`
 * because the longer suffix would otherwise be read as five minutes.
 */
export const CRYPTO_PRICE_WINDOWS = [
  { id: '5min', labelKey: 'markets.crypto_price_window_5min' },
  { id: '15min', labelKey: 'markets.crypto_price_window_15min' },
  { id: 'hourly', labelKey: 'markets.crypto_price_window_hourly' },
  { id: 'daily', labelKey: 'markets.crypto_price_window_daily' },
]

export const CRYPTO_PRICE_ASSETS = [
  { id: 'bitcoin', label: 'BTC' },
  { id: 'ethereum', label: 'ETH' },
  { id: 'solana', label: 'SOL' },
]

const WINDOW_MATCH_ORDER = ['15min', '5min', 'hourly', 'daily']
const ASSET_IDS = new Set(CRYPTO_PRICE_ASSETS.map(asset => asset.id))

export const parsePriceLeafSlug = slug => {
  if (typeof slug !== 'string' || !slug) return null
  for (const windowId of WINDOW_MATCH_ORDER) {
    const suffix = `-${windowId}`
    if (!slug.endsWith(suffix)) continue
    const asset = slug.slice(0, -suffix.length)
    if (!ASSET_IDS.has(asset)) return null
    return { asset, window: windowId }
  }
  return null
}

export const categoryParentId = category => {
  const parent = category?.parent
  if (parent == null || parent === '') return null
  return typeof parent === 'object' ? parent.id : parent
}

export const isCryptoPriceCategory = children => {
  if (!Array.isArray(children) || children.length === 0) return false
  return children.every(child => parsePriceLeafSlug(child?.slug))
}

const leavesOf = children =>
  (children || [])
    .map(child => ({ child, parsed: parsePriceLeafSlug(child?.slug) }))
    .filter(row => row.parsed)

const sumCounts = categories => {
  if (categories.some(category => category?.open_events_count == null)) return null
  return categories.reduce((total, category) => total + Number(category.open_events_count), 0)
}

const rowsFor = (definitions, leaves, key) =>
  definitions
    .filter(definition => leaves.some(row => row.parsed[key] === definition.id))
    .map(definition => {
      const matched = leaves.filter(row => row.parsed[key] === definition.id).map(row => row.child)
      return {
        ...definition,
        ids: matched.map(category => category.id),
        count: sumCounts(matched),
      }
    })

/** Windows and assets that have at least one leaf, in display order. */
export const cryptoPriceFacetRows = children => {
  const leaves = leavesOf(children)
  return {
    windows: rowsFor(CRYPTO_PRICE_WINDOWS, leaves, 'window'),
    assets: rowsFor(CRYPTO_PRICE_ASSETS, leaves, 'asset'),
  }
}

/**
 * Moving between the time section and the asset section replaces the other
 * filter. A time click drops the asset; an asset click drops the time, so the
 * parent slug (`bitcoin-hourly`) cannot stay on as `window=hourly`. Clicking
 * the active row clears that section and leaves neither filter set.
 */
export const nextCryptoPriceSelection = (current, section, id) => {
  const selected = current?.[section] ?? null
  const next = selected === id ? null : id
  if (section === 'window') return { window: next, asset: null }
  return { window: null, asset: next }
}

/**
 * Category ids for the questions list. `null` when neither filter is set
 * (the parent id already includes every leaf). An empty array is a real
 * selection that matches no leaf, such as Hourly × BTC when that leaf is absent.
 */
export const cryptoPriceCategoryIds = (children, { window, asset } = {}) => {
  if (!window && !asset) return null
  return leavesOf(children)
    .filter(row => {
      if (window && row.parsed.window !== window) return false
      if (asset && row.parsed.asset !== asset) return false
      return true
    })
    .map(row => row.child.id)
}
